import Icon from 'components/atoms/Icon';
import PolicyDependentButton from 'components/molecules/PolicyDependentButtons/PolicyDependentButton';
import {
  AssessmentStageFragmentFragment,
  CognitiveMode,
  ProjectAction,
  ProjectStatus,
} from 'generated/graphql';
import { useProjectActionsValidation } from 'hooks/useProjectPolicyActions/useProjectPolicyActions';
import { FC, useState } from 'react';
import { Button } from '@spotted-zebra-uk/ui-components';
import StageActionModal from './AddStageActionModal';
import EditSkillsSelfAssessmentActionModal from './EditSkillsSelfAssessmentActionModal';
import StageAiInterviewAction from './StageAiInterviewAction';
import StageCoderbyteAction from './StageCoderbyteAction';
import StageCognitiveTestAction from './StageCognitiveTestAction';
import StageDeiFormAction from './StageDeiFormAction';
import StageEnglishProficiencyAction from './StageEnglishProficiencyAction';
import StagePersonalityTestAction from './StagePersonalityTestAction';
import StageRoleSkillsSelfAssessmentAction from './StageRoleSkillsSelfAssessmentAction';

interface IStageActions {
  stageId: string;
  projectId: number;
  assessmentStage: AssessmentStageFragmentFragment;
}

const StageActions: FC<IStageActions> = ({
  stageId,
  projectId,
  assessmentStage,
}) => {
  const [stageActionModalOpen, setStageActionModalOpen] = useState(false);
  const [
    editSkillsSelfAssessmentActionModalOpen,
    setEditSkillsSelfAssessmentActionModalOpen,
  ] = useState(false);

  const { projectPolicyActions, projectStatus } = useProjectActionsValidation({
    projectId,
  });

  const aiInterviewAction = assessmentStage.actions.aiInterview;
  const deiStageAction = assessmentStage.actions.dei;
  const personalityTest = assessmentStage.actions.personalityTest;
  const cognitiveTests = assessmentStage.actions.cognitiveTests ?? [];
  const rssAssessments = assessmentStage.actions.roleSkillSelfAssessments ?? [];
  const coderbyteAction = assessmentStage.actions.coderbyte;
  const englishProficiencyAction = assessmentStage.actions.englishProficiency;
  const hasDeiForm = deiStageAction && !deiStageAction.deletedAt;

  const sortedCognitiveTests = [...cognitiveTests].sort((testA, testB) => {
    return testA.kind > testB.kind ? 1 : -1;
  });

  return (
    <>
      <div className="stage-actions">
        <div className="stage-actions__header">
          <div className="stage-actions__title">Actions</div>
          <div className="stage-actions__header-right">
            <PolicyDependentButton
              id="stage-actions__add-assessment-stage-action-button"
              disabled={
                !projectPolicyActions[ProjectAction.AddAssessmentStageAction]
              }
              projectStatus={projectStatus}
              renderItem={disabled => (
                <Button
                  className="stage-actions__add-action-button"
                  onClick={() => setStageActionModalOpen(true)}
                  size="medium"
                  leftIcon={<Icon icon="add" />}
                  disabled={disabled}
                >
                  Add action
                </Button>
              )}
            />
          </div>
        </div>
        <div className="stage-actions__tests-list">
          {sortedCognitiveTests.map(cognitiveTest => (
            <StageCognitiveTestAction
              key={cognitiveTest.id}
              action={cognitiveTest}
              stageCognitiveMode={
                assessmentStage.cognitiveMode || CognitiveMode.InDepth
              }
              projectId={projectId}
              disabled={projectStatus === ProjectStatus.Closed}
            />
          ))}
          {personalityTest && (
            <StagePersonalityTestAction
              action={personalityTest}
              projectId={projectId}
              disabled={projectStatus === ProjectStatus.Closed}
            />
          )}
          {coderbyteAction && <StageCoderbyteAction action={coderbyteAction} />}
          {englishProficiencyAction && (
            <StageEnglishProficiencyAction action={englishProficiencyAction} />
          )}
          {rssAssessments.length > 0 && (
            <StageRoleSkillsSelfAssessmentAction
              rssAssessments={rssAssessments}
              stageId={stageId}
              projectId={projectId}
              onEditButtonClick={() =>
                setEditSkillsSelfAssessmentActionModalOpen(true)
              }
            />
          )}
          {hasDeiForm && (
            <StageDeiFormAction
              stageId={stageId}
              projectId={projectId}
              fields={deiStageAction?.fields}
              completedCount={deiStageAction?.submittedCount}
            />
          )}
          {aiInterviewAction && (
            <StageAiInterviewAction
              aiInterviewAction={aiInterviewAction}
              projectId={projectId}
            />
          )}
        </div>
      </div>
      {stageActionModalOpen && (
        <StageActionModal
          stageId={stageId}
          projectId={projectId}
          deiData={deiStageAction}
          availableActions={assessmentStage.availableActions}
          onClose={() => setStageActionModalOpen(false)}
        />
      )}
      {editSkillsSelfAssessmentActionModalOpen && (
        <EditSkillsSelfAssessmentActionModal
          stageId={stageId}
          projectId={projectId}
          rssAssessments={rssAssessments}
          availableActions={assessmentStage.availableActions}
          onClose={() => setEditSkillsSelfAssessmentActionModalOpen(false)}
        />
      )}
    </>
  );
};

export default StageActions;
